/**
 * @fileoverview Sign-in form content. Receives callbackUrl and error from parent (SignInForm) which reads useSearchParams.
 * @see SignInForm.tsx
 */
'use client';

import { authClient, signIn } from '@korvo/core/lib/better-auth/client';
import { useEffect, useState } from 'react';

import { defaultShellConfig,type ShellConfig } from '../../config/shell-config';
import { formatErrorDisplay, getServerErrorDetails } from './sign-in-error-utils';

function cn(...classes: Array<string | false | null | undefined>): string {
  return classes.filter(Boolean).join(' ');
}

export type SignInFormContentProps = {
  config?: ShellConfig | null;
  defaultCallbackUrl?: string;
  className?: string;
  /** Passed from SignInForm (from useSearchParams) so this component can render without dynamic() */
  callbackUrl?: string;
  paramError?: string;
  showRegisteredSuccess?: boolean;
}

export function SignInFormContent({
  config: configProp,
  defaultCallbackUrl,
  className,
  callbackUrl: callbackUrlProp = '/home',
  paramError: paramErrorProp = '',
  showRegisteredSuccess: showRegisteredSuccessProp = false,
}: SignInFormContentProps) {
  const config = configProp ?? defaultShellConfig;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const callbackUrl = callbackUrlProp;
  const paramError = paramErrorProp;
  const showRegisteredSuccess = showRegisteredSuccessProp;
  const displayError = error || paramError;

  useEffect(() => {
    let cancelled = false;
    void authClient.getSession().then((result) => {
      if (cancelled || !result.data?.user) {
        return;
      }
      const target = callbackUrl.startsWith('/') ? callbackUrl : `/${callbackUrl}`;
      window.location.replace(target);
    });
    return () => {
      cancelled = true;
    };
  }, [callbackUrl]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    if (!email || !password) {
      setError('Please fill in all fields');
      setIsLoading(false);
      return;
    }
    try {
      const result = await signIn.email({ email, password, rememberMe });
      if (result.error) {
        console.error('[SignIn] Authentication error:', result.error);
        setError('Invalid email or password. Please check your credentials and try again.');
        setIsLoading(false);
        return;
      }
      if (rememberMe) {
        document.cookie = `rememberMe=1; path=/; max-age=${60 * 60 * 24 * 90}`;
      } else {
        document.cookie = 'rememberMe=; path=/; max-age=0';
      }
      await new Promise((resolve) => setTimeout(resolve, 400));
      window.location.href = callbackUrl;
    } catch (err: unknown) {
      const details = await getServerErrorDetails(err);
      const message = details
        ? formatErrorDisplay(details.message, details.errorId)
        : 'An error occurred during sign in';
      setError(message);
      setIsLoading(false);
    }
  };

  return (
    <div className={cn('mx-auto max-w-7xl px-4 sm:px-6 lg:px-8', className)} style={{ paddingBottom: 'calc(var(--footer-height, 5rem) + 3rem)' }}>
      <div className="min-h-[60vh] flex items-center justify-center py-12" style={{ backgroundColor: 'var(--background-secondary)' }}>
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold" style={{ color: 'var(--foreground-default)' }}>Sign in to your account</h2>
            <p className="mt-2 text-center text-sm" style={{ color: 'var(--foreground-muted)' }}>{config.branding.brandingText || `Access your ${config.branding.appName} workspace`}</p>
          </div>
          <form className="mt-8 space-y-6" method="post" data-testid="signin-form" onSubmit={(e) => void handleSubmit(e)}>
            {showRegisteredSuccess && (
              <div data-testid="registration-success" className="px-4 py-3 rounded-md border" style={{ backgroundColor: 'var(--success-default)', borderColor: 'var(--success-default)', color: 'var(--success-foreground)' }}>Account successfully created. Sign in to continue.</div>
            )}
            {displayError && (
              <div data-testid="error-message" className="px-4 py-3 rounded-md border" style={{ backgroundColor: 'var(--danger-default)', borderColor: 'var(--danger-default)', color: 'var(--danger-foreground)' }}>{displayError}</div>
            )}
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>Email address</label>
                <input id="email" name="email" type="email" autoComplete="email" aria-label="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default focus:z-10 sm:text-sm" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--input-background)', color: 'var(--input-foreground)' }} placeholder="Enter your email" />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium" style={{ color: 'var(--label-foreground)' }}>Password</label>
                <input id="password" name="password" type="password" autoComplete="current-password" aria-label="Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 appearance-none relative block w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-primary-default focus:border-primary-default focus:z-10 sm:text-sm" style={{ borderColor: 'var(--input-border)', backgroundColor: 'var(--input-background)', color: 'var(--input-foreground)' }} placeholder="Enter your password" />
              </div>
              <div className="flex items-center">
                <input id="rememberMe" name="rememberMe" type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="h-4 w-4 focus:ring-primary-default rounded" style={{ accentColor: 'var(--primary-default)', borderColor: 'var(--input-border)' }} />
                <label htmlFor="rememberMe" className="ml-2 block text-sm" style={{ color: 'var(--label-foreground)' }}>Stay logged in</label>
              </div>
              <button type="submit" disabled={isLoading} className={cn('group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors', isLoading && 'opacity-50 cursor-not-allowed')} style={{ backgroundColor: 'var(--primary-default)', color: 'var(--primary-foreground)' }} onMouseEnter={(e) => { if (!isLoading) e.currentTarget.style.backgroundColor = 'var(--primary-hover)'; }} onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'var(--primary-default)'; }}>
                {isLoading ? <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" /> : 'Sign In'}
              </button>
            </div>
            <div className="text-center">
              <p className="text-sm" style={{ color: 'var(--foreground-muted)' }}>By signing in, you agree to our <a href={config.routes.terms} className="hover:underline" style={{ color: 'var(--link-default)' }}>Terms of Use</a> and <a href={config.routes.privacy} className="hover:underline" style={{ color: 'var(--link-default)' }}>Privacy Policy</a>.</p>
              <p className="text-xs mt-2" style={{ color: 'var(--foreground-muted)' }}>No account? <a href={config.routes.register} className="hover:underline" style={{ color: 'var(--link-default)' }}>Create one</a></p>
              <p className="text-xs mt-1" style={{ color: 'var(--foreground-muted)' }}><a href={config.routes.forgotPassword} className="hover:underline" style={{ color: 'var(--link-default)' }}>Forgot password?</a></p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
